import { MobileFrame } from "@faire/prototype-playground/components/MobileFrame";
import { LiveToolbar } from "@faire/prototype-playground/platform/components/toolbar/LiveToolbar";
import type { PrototypeMetadata } from "@faire/prototype-playground/workspace/types";
import { Button } from "@faire/slate/components/button";
import { Redo } from "@faire/slate-icons/Redo";
import { useEffect, useRef, useState } from "react";

import {
  CELEBRATION_VARIANTS,
  TRANSITION_CONTEXT_NOTE,
  TRANSITION_VARIANTS,
} from "./data";
import { LegalBusinessNameStepMock } from "./LegalBusinessNameStepMock";
import { RevealStage } from "./reveals";
import { useReplayKey, VariantExplainer } from "./shared";
import { TransitionStage } from "./transitions";

export const prototypeMeta: PrototypeMetadata = {
  name: "Net Terms Transition Reveal",
  owner: "Matthew Leandres",
  email: "matthew.leandres@faire.com",
  status: "in-progress",
  platform: "web",
  createdAt: "2026-08-05",
  lastUpdated: "2026-08-05",
  surfaceArea: "Retailer",
  category: "experimental",
  creationTool: "claude-code",
  thumbnail: "/images/thumbnails/mle-net-terms-transition-reveal.png",
  description:
    "Replaces the button-spinner wait between Legal Business Name and Net Terms with a full transition screen (spinner, title, rotating subtitles) and a confetti celebration on reveal. Several variants of each, with hypothesis notes.",
};

const VIEWPORT_DESKTOP = 1;
const VIEWPORT_MOBILE = 2;

const BUTTON_LOADING_DELAY_MS = 350;

type FlowPhase = "form" | "transition" | "reveal";

export default function NetTermsTransitionRevealPage() {
  const [phase, setPhase] = useState<FlowPhase>("form");
  const [isSubmittingForm, setIsSubmittingForm] = useState(false);
  const [transitionVariantNumber, setTransitionVariantNumber] = useState(1);
  const [celebrationVariantNumber, setCelebrationVariantNumber] = useState(1);
  const [viewport, setViewport] = useState(VIEWPORT_DESKTOP);
  const [replayKey, replay] = useReplayKey();
  const submitTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    document.title = prototypeMeta.name;
  }, []);

  useEffect(() => () => clearTimeout(submitTimeoutRef.current), []);

  const transitionVariant =
    TRANSITION_VARIANTS[transitionVariantNumber - 1] ?? TRANSITION_VARIANTS[0];
  const celebrationVariant =
    CELEBRATION_VARIANTS[celebrationVariantNumber - 1] ??
    CELEBRATION_VARIANTS[0];

  const handleContinueFromForm = (): void => {
    clearTimeout(submitTimeoutRef.current);
    setIsSubmittingForm(true);
    submitTimeoutRef.current = setTimeout(() => {
      setIsSubmittingForm(false);
      setPhase("transition");
    }, BUTTON_LOADING_DELAY_MS);
  };

  const handleReplay = (): void => {
    clearTimeout(submitTimeoutRef.current);
    replay();
    setIsSubmittingForm(false);
    setPhase("form");
  };

  useEffect(() => {
    if (phase !== "transition") {
      return;
    }
    const timer = setTimeout(() => {
      setPhase("reveal");
    }, transitionVariant.durationMs);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- replayKey forces the wait to restart on every replay
  }, [phase, transitionVariant.durationMs, replayKey]);

  const stage = (
    <div
      key={`${phase}-${transitionVariantNumber}-${celebrationVariantNumber}-${replayKey}`}
      className="flex h-full flex-col"
    >
      {phase === "form" ? (
        <LegalBusinessNameStepMock
          onContinue={handleContinueFromForm}
          isSubmitting={isSubmittingForm}
        />
      ) : phase === "transition" ? (
        <TransitionStage variant={transitionVariant.key} />
      ) : (
        <RevealStage variant={celebrationVariant.key} />
      )}
    </div>
  );

  return (
    <>
      <LiveToolbar
        variantGroups={[
          {
            title: "Transition style",
            queryKey: "transition",
            variants: TRANSITION_VARIANTS.map((config, index) => ({
              variantNumber: index + 1,
              label: config.label,
            })),
            active: transitionVariantNumber,
            onChange: setTransitionVariantNumber,
          },
          {
            title: "Celebration style",
            queryKey: "celebration",
            variants: CELEBRATION_VARIANTS.map((config, index) => ({
              variantNumber: index + 1,
              label: config.label,
            })),
            active: celebrationVariantNumber,
            onChange: setCelebrationVariantNumber,
          },
        ]}
        stateGroups={[
          {
            title: "Viewport",
            queryKey: "viewport",
            states: [
              { stateNumber: VIEWPORT_DESKTOP, label: "Desktop" },
              { stateNumber: VIEWPORT_MOBILE, label: "Mobile web" },
            ],
            active: viewport,
            onChange: setViewport,
          },
        ]}
      />
      <div className="bg-fs-surface-secondary relative min-h-screen">
        <aside className="fixed top-(--slate-dimensions-6) right-(--slate-dimensions-6) z-10 hidden w-[min(100%-48px,340px)] flex-col gap-(--slate-dimensions-4) md:flex">
          <VariantExplainer
            eyebrow={TRANSITION_CONTEXT_NOTE.eyebrow}
            title={TRANSITION_CONTEXT_NOTE.title}
            hypothesis={TRANSITION_CONTEXT_NOTE.body}
          />
          <VariantExplainer
            eyebrow="Transition style"
            title={transitionVariant.label}
            hypothesis={transitionVariant.hypothesis}
          />
          <VariantExplainer
            eyebrow="Celebration style"
            title={celebrationVariant.label}
            hypothesis={celebrationVariant.hypothesis}
          />
          <Button
            variant="secondary"
            type="button"
            onClick={handleReplay}
            iconProps={{ Component: Redo, position: "start" }}
          >
            Replay from Legal Business Name
          </Button>
        </aside>

        <div className="flex min-h-screen items-center justify-center md:py-(--slate-dimensions-10)">
          {viewport === VIEWPORT_MOBILE ? (
            <MobileFrame url="faire.com/welcome">{stage}</MobileFrame>
          ) : (
            <div className="bg-fs-surface-primary min-h-[720px] w-full max-w-[480px] overflow-hidden rounded-(--slate-dimensions-4) shadow-lg">
              {stage}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
